import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Mail, X } from 'lucide-react'
import Modal from './ui/Modal'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { useUserDirectory } from '../hooks/useUserDirectory'

// In-app guaranteed delivery for direct_messages.
//
// Push notifications are best-effort — the partner's send to the owner can
// silently fail (subscription drift, OS quotas, network) and the
// owner never finds out a message exists. The dialog this component
// mounts is the *separate* delivery path: on app open and on every
// new realtime insert, we fetch unread messages addressed to the
// viewer and present them one at a time. Close is the only action;
// it flips read_at server-side and dequeues to the next unread.
//
// Mounted in App.jsx inside the authed tree, so it never appears on
// the login screen.
export default function UnreadMessageGate() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { resolve } = useUserDirectory()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'

  const [queue, setQueue] = useState([])
  const [busyId, setBusyId] = useState(null)
  // Track ids we've already enqueued so a refetch + a realtime event
  // for the same row don't push a duplicate dialog.
  const seenIds = useRef(new Set())

  const enqueueRows = useCallback((rows) => {
    if (!rows?.length) return
    setQueue((prev) => {
      const out = [...prev]
      for (const row of rows) {
        if (seenIds.current.has(row.id)) continue
        seenIds.current.add(row.id)
        out.push(row)
      }
      // Oldest first — chronological is less confusing than LIFO
      // when several land while the user was offline.
      out.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      return out
    })
  }, [])

  const fetchUnread = useCallback(async () => {
    if (!user) return
    const { data } = await supabase
      .from('direct_messages')
      .select('id, sender_id, recipient_id, title, body, image_url, audio_url, image_view_limit, dispatched_at, scheduled_for, created_at, read_at')
      .eq('recipient_id', user.id)
      .is('read_at', null)
      .order('created_at', { ascending: true })
      .limit(20)
    // Only surface messages that are actually live — a scheduled
    // send that hasn't been dispatched yet shouldn't pop the dialog.
    const live = (data || []).filter((r) => r.dispatched_at || !r.scheduled_for)
    enqueueRows(live)
  }, [user, enqueueRows])

  // Initial fetch + refetch whenever the tab/PWA returns to the
  // foreground. visibilitychange is the closest browser signal to
  // "user just opened the app" for an installed PWA — iOS doesn't
  // fire focus reliably on cold launch from the home screen, but it
  // does fire visibility.
  useEffect(() => {
    if (!user) {
      seenIds.current = new Set()
      setQueue([])
      return undefined
    }
    fetchUnread()
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchUnread()
    }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('focus', fetchUnread)
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('focus', fetchUnread)
    }
  }, [user, fetchUnread])

  // Realtime: surface the dialog the moment a message becomes live.
  // Listen to INSERT (immediate sends) AND UPDATE (a scheduled send
  // the cron just dispatched flips dispatched_at). Only enqueue rows
  // that are live + still unread.
  useEffect(() => {
    if (!user) return undefined
    const consider = (row) => {
      if (!row || row.read_at) return
      if (!row.dispatched_at && row.scheduled_for) return
      enqueueRows([row])
    }
    const channel = supabase
      .channel(`dm-inbox-${user.id}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'direct_messages',
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => consider(payload?.new),
      )
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [user, enqueueRows])

  const current = queue[0] || null

  const senderName = useMemo(() => {
    if (!current) return null
    return resolve(current.sender_id, locale).name || current.sender_id.slice(0, 8) + '…'
  }, [current, locale, resolve])

  const formattedTime = useMemo(() => {
    if (!current) return ''
    try {
      const d = new Date(current.created_at)
      return d.toLocaleString(locale === 'ar' ? 'ar-EG' : 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    } catch {
      return current.created_at
    }
  }, [current, locale])

  const handleClose = async () => {
    if (!current || busyId === current.id) return
    setBusyId(current.id)
    // Optimistic dequeue first so the next message can render even if
    // the update lags. The row is gone from the queue forever via
    // seenIds even if the network write fails — but the recipient_id
    // update policy means the next reload will skip it anyway since
    // read_at flipped on the server.
    const id = current.id
    setQueue((prev) => prev.filter((r) => r.id !== id))
    await supabase
      .from('direct_messages')
      .update({ read_at: new Date().toISOString() })
      .eq('id', id)
    setBusyId(null)
  }

  const handleOpenInInbox = () => {
    if (!current) return
    const senderId = current.sender_id
    handleClose()
    navigate(`/messages/${senderId}`)
  }

  if (!current) return null

  return (
    <Modal
      open
      onClose={handleClose}
      closeOnScrim={false}
      closeOnEsc={false}
      ariaLabel={t('messages.gate.aria')}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="p-1.5 rounded-md bg-app-primary-500/10 text-app-primary-500 flex-shrink-0">
          <Mail className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-muted-foreground" dir="auto">
            {t('messages.gate.fromLine', { name: senderName })}
          </p>
          <p className="text-[11px] text-muted-foreground" dir="auto">
            {formattedTime}
          </p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          aria-label={t('settings.close')}
          disabled={busyId === current.id}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground disabled:opacity-50"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {current.title && (
        <h2 className="text-lg font-bold text-foreground leading-tight mb-2" dir="auto">
          {current.title}
        </h2>
      )}
      {current.image_url && (
        current.image_view_limit ? (
          // Never reveal a view-once photo here — it must be opened
          // (and counted) from the thread. Show a hint instead.
          <div className="rounded-lg mb-3 px-3 py-2 bg-app-primary-500/10 text-app-primary-500 text-[13px] inline-flex items-center gap-1.5" dir="auto">
            <Mail className="w-4 h-4" /> {t('messages.gate.viewOncePhoto')}
          </div>
        ) : (
          <img
            src={current.image_url}
            alt=""
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            className="rounded-lg mb-3 max-h-60 w-auto object-cover select-none [-webkit-touch-callout:none]"
          />
        )
      )}
      {current.audio_url && (
        <audio src={current.audio_url} controls className="w-full mb-3" />
      )}
      {current.body && (
        <p
          className="text-sm text-foreground whitespace-pre-wrap leading-relaxed mb-4 max-h-[50vh] overflow-y-auto"
          dir="auto"
        >
          {current.body}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleOpenInInbox}
          disabled={busyId === current.id}
          className="flex-1 py-2 rounded-md border-2 border-border text-sm text-foreground hover:bg-accent disabled:opacity-50"
          dir="auto"
        >
          {t('messages.gate.openInbox')}
        </button>
        <button
          type="button"
          onClick={handleClose}
          disabled={busyId === current.id}
          className="flex-1 py-2 rounded-md bg-app-primary-500 text-white text-sm font-semibold disabled:opacity-50"
          dir="auto"
        >
          {t('messages.gate.close')}
        </button>
      </div>

      {queue.length > 1 && (
        <p className="text-[11px] text-muted-foreground text-center mt-2" dir="auto">
          {t('messages.gate.moreCount', { count: queue.length - 1 })}
        </p>
      )}
    </Modal>
  )
}
